import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {ClearingAllocationTaskService} from "../../../services/clearing/clearing-allocation-task.service";
import {AllocationTask} from "../../../model/clearing/allocation/allocation-task";
import {DatePipe, DecimalPipe} from "@angular/common";
import {FieldRecord} from "../../../model/api/field-record";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {ClearingAllocationService} from "../../../services/clearing/clearing-allocation.service";
import {ThemeService} from "../../../services/tools/theme.service";
import {AppConfigService} from "../../../services/app-config.service";
import {ClearingTableConfigService} from "../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../services/clearing/clearing-selection.service";
import {ClearingTableSyncWsService} from "../../../services/clearing/ws/clearing-table-sync-ws.service";
import {ClearingTableBaseWsAbsComponent} from "./clearing-table-base-ws-abs.component";
import {TableWsService} from "../../../services/websocket/table-ws.service";
import {EMPTY, exhaustMap, Observable, switchMap, take, takeUntil, timer} from "rxjs";
import {catchError, filter, map, tap} from "rxjs/operators";
import {ApiValidation} from "../../../model/api/api-validation";

@Component({
  template: 'nop',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClearingTableBaseWsComponent extends ClearingTableBaseWsAbsComponent {

  protected minItemCountForSummary: number = 2;
  private initialWsLoadTriggered = false;

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              protected confirmationService: ConfirmationService,
              public appConfigService: AppConfigService,
              public clearingWsService: TableWsService,
              translateService: TranslateService,
              protected commonFieldService: CommonFieldService,
              protected allocationTaskService: ClearingAllocationTaskService,
              protected allocationService: ClearingAllocationService,
              public themeService: ThemeService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService) {
    super(cdref, messageService, appConfigService, clearingWsService, translateService,
      commonFieldService, tableConfigService, clearingCookieService, clearingTableSyncWsService, clearingSelectionService);
  }

  ngAfterContentInit() {
    super.ngAfterContentInit();

    setTimeout(() => {
      this.loadSettingsFromCookies();

      if (this.viewConfig) {
        this.refreshTable();
        this.triggerInitialWsLoad();
      } else {
        this.refreshConfig();
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected override refreshConfig() {
    this.getViewTemplate().pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.viewConfig = apiValidation.item;
        this.refreshTable();
        this.triggerInitialWsLoad();
      } else {
        this.errorMessage(apiValidation.message);
        this.setNotLoading();
      }
    });

    this.clearingStateColorMap = this.appConfigService.clearingStateColorMap;
  }

  startCheckingAllocationTask(
    taskId: string,
    onError?: (allocationTask: AllocationTask) => void,
  ): void {
    timer(0, this.appConfigService.clearingAllocationCheckInterval)
      .pipe(
        takeUntil(this.destroy$),
        take(this.appConfigService.clearingAllocationCheckCallLimit), // max number of checks

        // one HTTP call at a time; ignore ticks while a call is in flight
        exhaustMap(() =>
          this.allocationTaskService.check(taskId).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              return EMPTY;
            }),
          ),
        ),

        tap(apiValidation => {
          if (!apiValidation.success) {
            this.errorMessage(apiValidation.message);
          }
        }),
        filter(apiValidation => apiValidation.success),

        map(apiValidation => apiValidation.item as AllocationTask | null),

        // keep polling if null or IN_PROGRESS
        filter(task => task != null && task.state !== AllocationTask.STATE.IN_PROGRESS),

        take(1), // first terminal state wins (TREATED or ERROR)
      )
      .subscribe(task => {
        if (!task) return;

        if (task.state === AllocationTask.STATE.TREATED) {
          // Nothing to do (same as your current code)
          return;
        }

        if (task.state === AllocationTask.STATE.ERROR) {
          this.infoMessage(task.message);
          timer(250)
            .pipe(takeUntil(this.destroy$), take(1))
            .subscribe(() => onError?.(task));
          return;
        }

        // Safety: unexpected terminal state
        this.errorMessage(`Allocation task ${taskId} ended in unexpected state: ${task.state}`);
      });
  }

  protected afterAllocationTask<T>(
    taskId: any,
    request$: () => Observable<ApiValidation<T>>,
    onSuccess: (item: T) => void,
  ): void {
    this.startCheckingAllocationTask(taskId, () => {
      timer(250)
        .pipe(
          takeUntil(this.destroy$),
          switchMap(() => request$()),
          tap(v => {
            if (!v.success) this.errorMessage(v.message);
          }),
          filter(v => v.success),
        )
        .subscribe(v => onSuccess(v.item));
    });
  }

  selectTableColumns() {
    const columnFields: FieldRecord<any>[] = this.viewConfig.templateRows.map(value => new FieldRecord(value.viewColumn.field, value.filterName));
    let visibleColumns = this.clearingCookieService.loadVisibleColumns(this.userView.id, this.origin);
    if (!visibleColumns || visibleColumns.length === 0) {
      visibleColumns = this.viewConfig.templateRows.filter(v => !v.disabled).map(v => v.colName);
    }
    this.selectTableColumnsDialog.open(columnFields, visibleColumns);
  }

  onSelectTableColumns(event: any) {
    if (event && event.length > 0) {
      this.clearingCookieService.storeVisibleColumns(this.userView.id, this.origin, event);
    } else {
      this.clearingCookieService.clearVisibleColumns(this.userView.id, this.origin);
      event = this.viewConfig.templateRows.filter(v => !v.disabled).map(v => v.colName);
    }
    this.visibleColumns = event;
    this.refreshTable();
    this.clearReloadTable(this.tableCurrentRequest);
  }

  protected loadSettingsFromCookies(): void {
    if (this.userView == null)
      return;
    this.visibleColumns = this.clearingCookieService.loadVisibleColumns(this.userView.id, this.origin);

    const restoredRequest = this.clearingCookieService.loadFilterSortState(this.userView.id, this.origin);
    if (restoredRequest) {
      this.ccTable.setFiltersAndSort(restoredRequest);
      this.tableCurrentRequest = restoredRequest;
    }

    if (this.clearingCookieService.isMaximized(this.userView.id, this.origin)) {
      this.maximize();
    }
  }

  confirmCommand(
    action: () => void,
    titleKey: string,
    messageKey: string,
    submitKey = 'common.dialog.ok',
    cancelKey = 'common.dialog.cancel'
  ): void {
    this.confirmationService.confirm({
      header: this.getTranslation(titleKey),
      message:
        this.getTranslation(messageKey),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation(submitKey),
      rejectLabel: this.getTranslation(cancelKey),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: () => action()
    });
  }

  maximize() {
    this.maximized = !this.maximized;
    this.maximizedChange.emit(this.maximized);
    this.clearingCookieService.storeMaximizedState(this.userView.id, this.origin, this.maximized);
  }

  hasMultipleOwners(): boolean {
    const owners = new Set(this.currentClearingSelectionInfosByLeg.map(x => x.owner));
    return owners.size > 1;
  }

  private triggerInitialWsLoad(): void {
    if (this.initialWsLoadTriggered) {
      return;
    }

    if (!this.tableConfig) {
      return;
    }

    this.initialWsLoadTriggered = true;
    this.setLoading();
    this.clearingTableSyncWsService.syncRecordsRequestWs(this);
    this.clearingTableSyncWsService.refreshCurrentView(this);
    this.markForCheck();
  }
}
